import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, PageHeader } from "@/components/common/ui-bits";
import { EntryRow } from "@/components/entry/EntryRow";
import { MyScans } from "@/components/scan/MyScans";
import { EditEntryDialog } from "@/components/entry/EditEntryDialog";
import { api } from "@/lib/api";

const PAGE = 30;

export default function MyEntriesPage() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (p) => {
    const { data } = await api.get("/transactions/mine", { params: { page: p, page_size: PAGE } });
    setItems((prev) => (p === 1 ? data.items : [...(prev || []), ...data.items]));
    setTotal(data.total_items);
    setPage(p);
  }, []);

  useEffect(() => { load(1); }, [load]);

  const onSaved = (updated) => {
    setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)));
    setEditing(null);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader eyebrow="Entries you recorded" title="My Entries" subtitle="Tap Edit to fix a mistake." testId="my-entries-title">
        <Button asChild className="rounded-xl" data-testid="my-entries-add-button"><Link to="/add">Add Entry</Link></Button>
      </PageHeader>
      <MyScans />
      <div className="surface divide-y divide-border overflow-hidden fade-up" data-testid="my-entries-list">
        {items === null && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {items?.length === 0 && <EmptyState icon={Inbox} text="No transactions found" testId="my-entries-empty" />}
        {items?.map((e) => (
          <EntryRow key={e.id} entry={e} actions={e.bill_id ? <span className="text-[11px] font-semibold text-muted-foreground" data-testid={`my-entry-bill-payment-${e.id}`}>Bill payment · change it from the bill</span> :
            <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => setEditing(e)} data-testid={`my-entry-edit-${e.id}`}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          } />
        ))}
      </div>
      {items && items.length < total && (
        <div className="mt-4 text-center">
          <Button variant="outline" onClick={() => load(page + 1)} data-testid="my-entries-load-more">Load more</Button>
        </div>
      )}
      <EditEntryDialog entry={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
    </div>
  );
}
